/* SUMMARY
  * columnKey: stable identity of a descriptor column
  * readExcludedColumns / writeExcludedColumns: persistence
  * selectDescriptor: apply a selection to a descriptor
*/

/**
 * Mémorisation du choix de colonnes d'un export, par entité.
 *
 * Ce qui est stocké, ce sont les colonnes **exclues**, jamais les incluses. C'est le point central
 * de ce module : un descripteur gagne des colonnes avec le temps — partExportDescriptor est passé
 * de trois à sept la semaine dernière — et une liste d'incluses figée dans le navigateur d'un
 * utilisateur masquerait définitivement toute colonne ajoutée après sa dernière visite, sans
 * qu'aucun test ne le voie. En stockant les exclues, une nouvelle colonne arrive cochée : le défaut
 * reste « tout », et seul un refus explicite persiste.
 *
 * L'identité d'une colonne est son `column`, pas son en-tête traduit : celui-ci change avec la
 * langue, et un choix fait en français ne doit pas se perdre au passage en néerlandais.
 */

const STORAGE_PREFIX = "exportFields";

/** Clé de stockage d'une entité. `entityName` est déjà unique par vue ("workorders", "users"…). */
const storageKey = (entityName: string): string => `${STORAGE_PREFIX}:${entityName}`;

/**
* columnKey: identité stable d'une colonne, indépendante de la langue d'affichage.
* @param descriptor - entrée de descripteur
*/
export const columnKey = (descriptor: EasyTech.ExportDescriptor): string => descriptor.column;

/**
* readExcludedColumns: colonnes que l'utilisateur a explicitement décochées pour cette entité.
*
* Tolère un stockage absent, illisible ou corrompu : un mauvais JSON ne doit pas empêcher un export,
* il doit simplement ramener au défaut « tout coché ».
*
* @param entityName - entité concernée, ex. "workorders"
*/
export const readExcludedColumns = (entityName: string): string[] => {
  try {
    const raw = localStorage.getItem(storageKey(entityName));
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];

    return parsed.filter((entry): entry is string => typeof entry === "string");
  } catch {
    return [];
  }
};

/**
* writeExcludedColumns: mémorise les colonnes décochées, ou efface l'entrée quand il n'y en a plus.
*
* Écrire un tableau vide plutôt que supprimer laisserait une clé inutile dans le stockage de chaque
* utilisateur ayant tout recoché une fois.
*
* @param entityName - entité concernée
* @param excluded - colonnes décochées
*/
export const writeExcludedColumns = (entityName: string, excluded: string[]): void => {
  try {
    if (excluded.length === 0) {
      localStorage.removeItem(storageKey(entityName));
      return;
    }
    localStorage.setItem(storageKey(entityName), JSON.stringify(excluded));
  } catch {
    // Stockage plein ou refusé (navigation privée) : le choix ne survivra pas à la modale, ce qui
    // est acceptable — l'export lui-même n'a aucune raison d'échouer pour autant.
  }
};

/**
* selectDescriptor: ne garde que les colonnes retenues, dans l'ordre du descripteur.
*
* L'ordre vient toujours du descripteur, jamais de l'ordre de cochage : deux exports du même écran
* doivent produire les mêmes colonnes dans le même ordre.
*
* @param descriptor - descripteur complet
* @param selected - clés des colonnes à inclure
*/
export const selectDescriptor = (
  descriptor: EasyTech.ExportDescriptor[],
  selected: string[]
): EasyTech.ExportDescriptor[] => {
  const keep = new Set(selected);
  return descriptor.filter((entry) => keep.has(columnKey(entry)));
};
